import { useState, useMemo } from "react";
import { Button, CheckCircleIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { SmallButtonProps } from "@/types/common-types";

import styles from "./buttonWrapper.module.css";

function SmallButtonWrapper({
  activeLabel,
  inactiveLabel,
  className,
  isDisabled = false,
  hierarchy = "primary",
  semanticColor,
  isRightIcon = false,
  isClickableDisabled = false,
  rightIcon,
  state,
  onClick,
  buttonWidthAuto = false,
  ...otherProps
}: Readonly<SmallButtonProps & { isClickableDisabled?: boolean }>) {
  const [isShowRightIcon, setIsShowRightIcon] = useState(false);
  const [isKeyBoardFocused, setIsKeyBoardFocused] = useState(true);
  const { current } = useWizard();
  const makeButtonWidthFitContent =
    current.uiElementControls?.makeButtonWidthFitContent;

  useMemo(() => {
    setIsShowRightIcon(state ?? false);
  }, [state]);

  const style = useMemo(() => {
    if (hierarchy === "primary") {
      if (semanticColor === "Dark Background") return "";
      if (isShowRightIcon && !buttonWidthAuto) {
        return `${
          styles.primary
        } bg-interactive-primary-default-1 border-interactive-primary-default-1
            ${
              makeButtonWidthFitContent ? "w-fit" : "w-[123px]"
            } h-[40px] rounded-radius-pill text-l pt-spacing-s pr-spacing-xs pb-spacing-s pl-spacing-s font-semibold font-body-font
            text-surface-neutral-option-10
            hover:bg-interactive-primary-hovered
            active:bg-interactive-primary-pressed-2 active:border-bg-interactive-primary-pressed-2
            flex flex-col justify-center items-center gap-grid-system-space-050
            disabled:text-text-disabled disabled:bg-surface-neutral-option-5 border-none outline-none ${
              styles.right_Icon_margin
            }`;
      }
      let widthClass = !buttonWidthAuto ? "w-[74px]" : "";

      if (makeButtonWidthFitContent) widthClass = "w-fit";

      return `${styles.primary} bg-interactive-primary-default-1 border-interactive-primary-default-1
        ${widthClass} h-[40px] rounded-radius-pill  text-l pt-spacing-s pr-spacing-s pb-spacing-s pl-spacing-s font-semibold font-body-font
        text-surface-neutral-option-10
        hover:bg-interactive-primary-hovered
        active:bg-interactive-primary-pressed-2 active:border-bg-interactive-primary-pressed-2
        flex flex-col justify-center items-center gap-grid-system-space-050
        disabled:text-text-disabled disabled:bg-surface-neutral-option-5 border-none outline-none`;
    }

    if (hierarchy === "secondary") {
      if (semanticColor === "Dark Background") return ``;
      if (isShowRightIcon) {
        return `${styles.secondary} bg-interactive-primary-default-1 border-interactive-primary-default-1
        w-full h-[40px] rounded-radius-pill text-l pt-spacing-s pr-spacing-xs pb-spacing-s pl-spacing-s font-semibold font-body-font
        active:bg-interactive-primary-default-1 active:bg-interactive-primary-default-1 
        flex flex-col justify-center items-center gap-grid-system-space-050
        disabled:text-text-disabled disabled:bg-surface-neutral-option-5 border-none outline-none`;
      }
      return `${styles.secondary} bg-interactive-primary-default-1 border-interactive-primary-default-1
      w-full h-[40px] rounded-radius-pill spacing-s text-lpt-spacing-s pr-spacing-s pb-spacing-s pl-spacing-s font-semibold font-body-font
      active:bg-interactive-primary-default-1 active:bg-interactive-primary-default-1 
      flex flex-col justify-center items-center gap-grid-system-space-050
      disabled:text-text-disabled disabled:bg-surface-neutral-option-5 border-none outline-none`;
    }
    if (hierarchy === "tertiary") {
      if (semanticColor === "Dark Background") {
        return `
        ${styles.tertiary_dark} border-none bg-none px-[12px] text-dark-text-on-primary relative hover:text-interactive-secondary-default
        focus:rounded-radius-pill focus:border-solid outline-none`;
      }
      return "empty";
    }
    return "empty";
  }, [
    hierarchy,
    semanticColor,
    isShowRightIcon,
    buttonWidthAuto,
    makeButtonWidthFitContent,
  ]);

  const icon = useMemo(() => {
    if (isShowRightIcon && isRightIcon && !rightIcon) {
      return (
        <CheckCircleIcon
          fill="rgba(39, 36, 39, 1)"
          stroke="rgba(246, 176, 82, 1)"
        />
      );
    }
    if (isShowRightIcon && isRightIcon && rightIcon) {
      return rightIcon;
    }

    return <></>;
  }, [isShowRightIcon, isRightIcon, rightIcon]);

  const label = useMemo(() => {
    if (isShowRightIcon) return activeLabel ?? "Test 2";

    return inactiveLabel ?? "Test 3";
  }, [isShowRightIcon, activeLabel, inactiveLabel]);

  const handleClick = () => {
    const value = !isShowRightIcon;
    if (onClick && !isClickableDisabled) {
      onClick(value);
    }
  };

  const handleOnPressStart = (e: { pointerType: string }) => {
    if (e.pointerType === "keyboard") {
      setIsKeyBoardFocused(true);
    } else {
      setIsKeyBoardFocused(false);
    }
  };

  return (
    <Button
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...otherProps}
      // data-testid="test-btn-component-default-click"
      btnType="label"
      label={label}
      type="button"
      isDisabled={isDisabled}
      className={`${style} ${className} ${styles.CoverSelectionBtnWrapper} ${
        styles.focus_class
      } ${isKeyBoardFocused ? "keyBoardFocused" : ""}`}
      hierarchy={hierarchy}
      semanticColor={semanticColor}
      onClick={handleClick}
      rightIcon={icon}
      isRightIcon
      onFocusChange={(e: boolean) => setIsKeyBoardFocused(e)}
      onPressStart={(e: { pointerType: string }) => handleOnPressStart(e)}
      isOnlyActiveOnPress
    />
  );
}

export default SmallButtonWrapper;
